import { AnimatePresence } from 'framer-motion'
// Lazy load pages for better performance
import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { ProtectedRoute } from '@/components/features/ProtectedRoute'
import { MainLayout } from '@/components/layout/MainLayout'

import { useAuth } from '@/contexts/AuthContext'

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'))
const AccountSetupPage = lazy(() => import('@/pages/auth/AccountSetupPage'))
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const CandidatesPage = lazy(() => import('@/pages/recruiter/CandidatesPage'))
const CandidateDetailPage = lazy(() => import('@/pages/recruiter/CandidateDetailPage'))
const ReferencesPage = lazy(() => import('@/pages/recruiter/ReferencesPage'))
const ReferenceDetailPage = lazy(() => import('@/pages/recruiter/ReferenceDetailPage'))
const CandidatePortal = lazy(() => import('@/pages/candidate/CandidatePortal'))
const RefereePortal = lazy(() => import('@/pages/referee/RefereePortal'))
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage'))
const AdminWorkspacesPage = lazy(() => import('@/pages/admin/AdminWorkspacesPage'))
const AdminTemplatesPage = lazy(() => import('@/pages/admin/AdminTemplatesPage'))
const AdminEmailTemplatesPage = lazy(() => import('@/pages/admin/AdminEmailTemplatesPage'))
const AdminFraudLogsPage = lazy(() => import('@/pages/admin/FraudLogsPage'))
const AdminAuditLogsPage = lazy(() => import('@/pages/admin/AuditLogsPage'))
const PrintableReferenceView = lazy(() => import('@/pages/PrintableReferenceView'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  )
}

export function AppRoutes() {
  const { isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <AnimatePresence mode="wait">
        {/* Key by pathname to avoid remounts on query changes */}
        <Routes location={location} key={location.pathname}>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/account-setup" element={<AccountSetupPage />} />

          {/* Token-based routes (no auth required but validated by token) */}
          <Route path="/candidate/reference/:token" element={<CandidatePortal />} />
          <Route path="/referee/:token" element={<RefereePortal />} />

          {/* Protected routes with layout */}
          <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/dashboard" element={<Navigate to="/" replace />} />
              <Route path="/settings" element={<Navigate to="/profile" replace />} />
              <Route path="/candidates" element={<CandidatesPage />} />
              <Route path="/candidates/:id" element={<CandidateDetailPage />} />
              <Route path="/references" element={<ReferencesPage />} />
              <Route path="/references/:id" element={<ReferenceDetailPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/workspace/members" element={<div>Workspace Members Page</div>} />
              <Route path="/workspace/settings" element={<div>Workspace Settings Page</div>} />
              <Route path="/notifications" element={<div>Notifications Page</div>} />
              <Route path="/help" element={<div>Help Page</div>} />

              {/* Admin Routes - Protected by role check in component */}
              <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/workspaces" element={<AdminWorkspacesPage />} />
              <Route path="/admin/templates" element={<AdminTemplatesPage />} />
              <Route path="/admin/email-templates" element={<AdminEmailTemplatesPage />} />
              <Route path="/admin/fraud-logs" element={<AdminFraudLogsPage />} />
              <Route path="/admin/audit-logs" element={<AdminAuditLogsPage />} />
            </Route>

            {/* Printable reference view - Protected but without MainLayout */}
            <Route path="/references/:id/print" element={<PrintableReferenceView />} />
          </Route>

          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  )
}
